<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-8">

    <!-- Page Header -->
    <div class="mb-8 flex items-center gap-4">
      <div class="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-700 rounded-2xl
                  flex items-center justify-center shadow-lg shadow-orange-500/30">
        <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
            d="M15 10l4.553-2.069A1 1 0 0121 8.82v6.36a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
        </svg>
      </div>
      <div>
        <h1 class="text-2xl font-bold text-slate-900">{{ video.id ? 'Update Video' : 'Add New Video' }}</h1>
        <p class="text-sm text-slate-500 mt-0.5">Manage your YouTube showcase videos</p>
      </div>
    </div>

    <!-- Form Card -->
    <div class="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden max-w-2xl">

      <!-- Card Header -->
      <div class="px-6 py-4 bg-gradient-to-r from-orange-500 to-orange-700 border-b border-orange-600">
        <div class="flex items-center gap-3">
          <svg class="w-5 h-5 text-white/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M15 10l4.553-2.069A1 1 0 0121 8.82v6.36a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"/>
          </svg>
          <span class="text-white font-semibold text-sm">Video Details</span>
        </div>
      </div>

      <!-- Loading Spinner -->
      <div v-if="loading" class="flex items-center justify-center py-24">
        <div class="flex flex-col items-center gap-3">
          <div class="w-10 h-10 border-4 border-orange-200 border-t-orange-600 rounded-full animate-spin"></div>
          <p class="text-sm text-slate-400">Loading…</p>
        </div>
      </div>

      <!-- Form -->
      <form v-else @submit.prevent="onSubmit" class="p-6 space-y-5">

        <!-- URL Field -->
        <div>
          <label class="block text-sm font-semibold text-slate-700 mb-2">
            <span class="flex items-center gap-1.5">
              <svg class="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M14.828 9.172a4 4 0 005.656 0l1-1a4 4 0 00-5.656-5.656l-1.1 1.1"/>
              </svg>
              YouTube Embed URL
            </span>
          </label>
          <input
            v-model="video.url"
            type="url"
            placeholder="https://www.youtube.com/embed/VIDEO_ID"
            class="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm text-slate-700
                   focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500 outline-none transition-all duration-200
                   placeholder:text-slate-300"
            :class="{ 'border-red-400 ring-2 ring-red-400/20': errors.url }"
          />
          <p v-if="errors.url" class="mt-1.5 text-xs text-red-500 font-medium">{{ errors.url[0] }}</p>
          <p class="mt-1.5 text-xs text-slate-400">Paste the YouTube embed URL (e.g. https://www.youtube.com/embed/abc123)</p>
        </div>

        <!-- Preview -->
        <div v-if="video.url" class="rounded-xl overflow-hidden border border-slate-200 shadow-sm">
          <div class="bg-slate-50 px-4 py-2 border-b border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Preview
          </div>
          <iframe :src="video.url" class="w-full h-48" frameborder="0" allowfullscreen loading="lazy"></iframe>
        </div>

        <!-- Actions -->
        <div class="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
          <RouterLink
            :to="{ name: 'app.youtube-videos' }"
            class="px-4 py-2.5 text-sm font-semibold text-slate-600 border border-slate-200 rounded-xl
                   hover:bg-slate-50 hover:border-slate-300 transition-all duration-200"
          >
            Cancel
          </RouterLink>
          <button
            type="submit"
            :disabled="loading"
            class="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600
                   hover:from-orange-600 hover:to-orange-700 text-white text-sm font-semibold rounded-xl
                   shadow-md shadow-orange-500/25 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5
                   disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
          >
            <svg v-if="loading" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"/>
            </svg>
            <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
            {{ video.id ? 'Update Video' : 'Save Video' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter, RouterLink } from 'vue-router'
import store from "@/store";
import { useToast } from "primevue/usetoast";

const route = useRoute()
const router = useRouter()
const toast = useToast()

const loading = ref(false)
const errors = ref({})
const video = ref({ id: null, url: '' })

function onSubmit() {
  loading.value = true
  errors.value = {}
  const action = video.value.id ? 'updateVideo' : 'createVideo'
  const successStatus = action === 'updateVideo' ? 200 : 201

  store.dispatch(action, video.value)
    .then((response) => {
      loading.value = false
      if (response.status === successStatus) {
        toast.add({ severity: 'success', summary: 'Saved', detail: 'Video saved successfully', life: 3000 })
        router.push({ name: 'app.youtube-videos' })
      }
    })
    .catch((err) => {
      loading.value = false
      if (err.response?.status === 422) {
        errors.value = err.response.data.errors
      }
    })
}

onMounted(() => {
  if (route.params.id) {
    loading.value = true
    store.dispatch('getVideo', route.params.id)
      .then((response) => {
        loading.value = false
        video.value = response.data
      })
  }
})
</script>
